import { FormEvent, ReactNode, RefObject, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { activateRole, fetchContext, nextStep } from '../lib/staffContext';
import { Lockup } from '../ui/Lockup';
import { LangToggle, useI18n } from '../i18n';
import { useDocumentTitle } from '../ui/a11y';
import { useFocusFieldOrHeading } from '../ui/focus';

// No signup form here on purpose — every account (admin, school_lead,
// teacher) is individually issued (design doc Section 4.7), not
// self-service. The admin role creates teacher/school_lead accounts from
// /admin; the first admin account is still a manual bootstrap step — see
// README.md.
//
// Two steps, per the platform-V2 decision in "Next Features — Discussion"
// (Claude Project doc): this is one shared app domain serving every school,
// so a school code ("slug") is entered first to tell schools apart, before
// the password step narrows it down to one person. The code lookup
// (get_school_by_slug) is anon-callable and deliberately returns the same
// "not found" shape whether the code is wrong or just doesn't exist —
// never a hint either way.
//
// After a successful password check we load the person's roles with
// my_context() (0006_v2_foundations.sql) — NOT by reading `profiles`, which
// the browser can't reach (that was the "admin shown as teacher" bug). Then:
//   - a school code was entered: the person must hold a role in that school,
//     otherwise they are signed back out; if they hold exactly one role
//     there, it becomes the active role;
//   - a single role overall is activated automatically by my_context();
//   - several roles and none chosen → the role chooser;
//   - a platform admin who must use two-factor → the two-factor screen.
// The platform admin isn't tied to any one school, so there's a skip link
// for that case.
type School = { id: string; name: string };
type Step = 'code' | 'credentials';

export function TeacherLogin() {
  const [step, setStep] = useState<Step>('code');
  const codeField = useRef<HTMLInputElement>(null);
  const emailField = useRef<HTMLInputElement>(null);

  const [schoolCode, setSchoolCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [school, setSchool] = useState<School | null>(null);
  const [adminSkip, setAdminSkip] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [credError, setCredError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const signedOutNotice = params.get('reason') === 'no_access' ? t['login.noAccess'] : null;

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setCheckingCode(true);
    setCodeError(null);

    const { data, error } = await supabase
      .rpc('get_school_by_slug', { p_slug: schoolCode })
      .maybeSingle<School>();

    setCheckingCode(false);

    if (error || !data) {
      // Deliberately the same message for "wrong code" and "no such code" —
      // see get_school_by_slug's comment in 0005_school_slugs.sql.
      setCodeError('login.codeError');
      return;
    }

    setSchool(data);
    setStep('credentials');
  }

  function skipToAdminLogin() {
    setSchool(null);
    setAdminSkip(true);
    setCodeError(null);
    setStep('credentials');
  }

  function changeSchool() {
    setStep('code');
    setSchool(null);
    setAdminSkip(false);
    setEmail('');
    setPassword('');
    setCredError(null);
  }

  async function handleCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setSigningIn(true);
    setCredError(null);

    const { data: signInData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError || !signInData.user) {
      setSigningIn(false);
      setCredError('login.credError');
      return;
    }

    const res = await fetchContext();
    if (res.kind !== 'ok') {
      await supabase.auth.signOut();
      setSigningIn(false);
      setCredError(res.kind === 'no_access' ? 'login.noAccess' : 'login.unavailable');
      return;
    }
    let ctx = res.ctx;

    if (school) {
      const here = ctx.roles.filter((r) => r.school_id === school.id);
      const isPlatformAdmin = ctx.roles.some((r) => r.role === 'platform_admin');
      if (here.length === 0 && !isPlatformAdmin) {
        await supabase.auth.signOut();
        setSigningIn(false);
        setCredError(`wrongSchool:${school.name}`);
        return;
      }
      if (here.length === 1 && ctx.active_grant_id !== here[0].grant_id) {
        const switched = await activateRole(here[0].grant_id);
        if (switched.kind === 'ok') ctx = switched.ctx;
      }
    }

    setSigningIn(false);
    navigate(nextStep(ctx));
  }

  // Error state holds a string key so the message follows the language switch.
  const credMessage =
    credError === null
      ? null
      : credError.startsWith('wrongSchool:')
        ? t['login.wrongSchool'](credError.slice('wrongSchool:'.length))
        : credError === 'login.noAccess'
          ? t['login.noAccess']
          : credError === 'login.unavailable'
            ? t['login.unavailable']
            : t['login.credError'];

  if (step === 'code') {
    return (
      <LoginFrame
        title={t['login.title']}
        subtitle={t['login.codeIntro']}
        focusField={codeField}
        step="code"
        foot={
          <>
            <p id="code-help">{t['login.codeHelp']}</p>
            <p>
              <button type="button" className="link-btn" onClick={skipToAdminLogin}>
                {t['login.adminSkip']}
              </button>
            </p>
          </>
        }
      >
        {signedOutNotice && (
          <p className="notice" role="status">
            {signedOutNotice}
          </p>
        )}
        <form className="form" onSubmit={handleCodeSubmit} noValidate={false}>
          <label className="field-label" htmlFor="school-code">
            {t['login.codeLabel']}
          </label>
          <input
            ref={codeField}
            id="school-code"
            className="field field-lg"
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={schoolCode}
            onChange={(e) => setSchoolCode(e.target.value)}
            placeholder={t['login.codePlaceholder']}
            required
            aria-invalid={codeError ? true : undefined}
            aria-describedby={codeError ? 'code-error code-help' : 'code-help'}
          />
          {codeError && (
            <p className="error-text" id="code-error" role="alert">
              {t['login.codeError']}
            </p>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={checkingCode}>
            {checkingCode ? t['login.checking'] : t['login.continue']}
          </button>
        </form>
      </LoginFrame>
    );
  }

  return (
    <LoginFrame title={t['login.title']} subtitle={t['login.credIntro']} focusField={emailField} step="credentials">
      {school ? (
        <div className="notice school-banner">
          <p>
            {t['login.signingInTo']} <strong>{school.name}</strong>
          </p>
          <button type="button" className="link-btn" onClick={changeSchool}>
            {t['login.notYourSchool']}
          </button>
        </div>
      ) : (
        adminSkip && (
          <div className="notice school-banner">
            <p>{t['login.adminBanner']}</p>
            <button type="button" className="link-btn" onClick={changeSchool}>
              {t['login.enterCodeInstead']}
            </button>
          </div>
        )
      )}
      <form className="form" onSubmit={handleCredentialsSubmit}>
        <label className="field-label" htmlFor="email">
          {t['login.email']}
        </label>
        <input
          ref={emailField}
          id="email"
          className="field field-lg"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-invalid={credMessage ? true : undefined}
          aria-describedby={credMessage ? 'cred-error' : undefined}
        />
        <label className="field-label" htmlFor="password">
          {t['login.password']}
        </label>
        <input
          id="password"
          className="field field-lg"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          aria-invalid={credMessage ? true : undefined}
          aria-describedby={credMessage ? 'cred-error' : undefined}
        />
        {credMessage && (
          <p className="error-text" id="cred-error" role="alert">
            {credMessage}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-lg" disabled={signingIn}>
          {signingIn ? t['login.signingIn'] : t['login.signIn']}
        </button>
      </form>
    </LoginFrame>
  );
}

function LoginFrame({
  title,
  subtitle,
  foot,
  focusField,
  step,
  children,
}: {
  title: string;
  subtitle: string;
  foot?: ReactNode;
  /** The step's first field: focused on arrival (and when the step changes). */
  focusField?: RefObject<HTMLElement>;
  step?: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const h1 = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(title);
  useFocusFieldOrHeading(h1, focusField, step);
  return (
    <div className="login-page">
      <main className="panel login-card fade-in">
        <div className="login-top">
          <Lockup />
          <LangToggle />
        </div>
        <div className="login-title">
          <h1 ref={h1} tabIndex={-1}>
            {title}
          </h1>
          <p className="text-muted">{subtitle}</p>
        </div>
        {children}
      </main>
      <footer className="login-foot">
        {foot}
        <p>
          <Link to="/accessibility">{t['a11y.link']}</Link>
        </p>
      </footer>
    </div>
  );
}
